import type { ContactFormData } from "@/store/auth/forms/useContactForm";
import { useContactForm } from "@/store/auth/forms/useContactForm";
import { useAuthState } from "@/store/auth";
import { useRoute } from "@/components/shared/router";
import { useCreateContactMutation } from "@/gqlhooks/profile/useCreateContactMutation";
import { useUpdateContactMutation } from "@/gqlhooks/profile/useUpdateContactMutation";
import { showToast } from "@/components/ui/toast/show-toast";
import {
  getErrorMessage,
  ensurePhoneNumberWithPlus,
} from "@/lib/helpers/string";
import { FormProvider } from "react-hook-form";
import { CreateContactScreen } from "./create-contact-screen";
import type { CreateContactProps } from "@/types/profile";
import { useApolloClient } from "@apollo/client";

export { type CreateContactProps };

export const CreateContact = () => {
  const { pull } = useRoute();
  const {
    data: { userInfo },
  } = useAuthState();
  const client = useApolloClient();

  const [createContactMutation, { loading: createContactLoading }] =
    useCreateContactMutation();
  const [updateContactMutation, { loading: updateContactLoading }] =
    useUpdateContactMutation();

  const defaultValues: ContactFormData = {
    firstName: "",
    lastName: "",
    primary: false,
    email: [{ value: "" }],
    phoneNumber: [{ value: "+65" }],
  };

  const contactFormMethods = useContactForm(defaultValues);
  const { handleSubmit } = contactFormMethods;

  const onSubmit = handleSubmit(async (data: ContactFormData) => {
    try {
      const formattedPhoneNumbers = data.phoneNumber?.map((phone) => {
        return ensurePhoneNumberWithPlus(phone.value);
      });

      const formattedEmails = data.email?.map(
        (email: { value: string }) => email.value,
      );

      const result = await createContactMutation({
        variables: {
          input: {
            clientId: userInfo.id,
            firstName: data.firstName ?? "",
            lastName: data.lastName ?? "",
            email: formattedEmails ?? [],
            phoneNumber: formattedPhoneNumbers ?? [],
            primary: data.primary ?? false,
          },
        },
      });

      if (result.data?.createContact?.contact) {
        if (result.data.createContact.contact.primary) {
          const existingContacts = userInfo.contacts || [];
          const lastPrimaryContact = existingContacts.find(
            (cont) => cont.primary,
          );

          try {
            if (lastPrimaryContact?.id) {
              await updateContactMutation({
                variables: {
                  input: {
                    id: lastPrimaryContact.id,
                    firstName: lastPrimaryContact.firstName,
                    lastName: lastPrimaryContact.lastName,
                    email: lastPrimaryContact.email,
                    phoneNumber: lastPrimaryContact.phoneNumber,
                    primary: false,
                  },
                },
              });
              // Evict and refetch the contacts cache
              client.cache.evict({ fieldName: "clientContacts" });
              client.cache.gc();
              pull();
              showToast({
                type: "success",
                title: "Success create contact",
              });
              return;
            }
          } catch (error) {
            const errMsg = getErrorMessage(error, "Error update contacts");
            showToast({
              title: errMsg,
              type: "error",
            });
            return;
          }
        }

        // Evict and refetch the contacts cache
        client.cache.evict({ fieldName: "clientContacts" });
        client.cache.gc();
        pull();

        showToast({
          title: "Success create contact",
          type: "success",
        });
      }
    } catch (error) {
      const errMsg = getErrorMessage(error, "Error create contact");
      showToast({
        title: errMsg,
        type: "error",
      });
    }
  });

  return (
    <FormProvider {...contactFormMethods}>
      <CreateContactScreen
        onSubmit={onSubmit}
        createContactLoading={createContactLoading || updateContactLoading}
      />
    </FormProvider>
  );
};
